import { useEffect, useRef, useState } from "react";
import {
    Box,
    TextField,
    Typography,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    ThemeProvider,
    createTheme,
    Checkbox,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Grid } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { makeGetRequestToUrl, makePostRequestToUrl } from '../commonFunc.jsx';
import config from '../../config/config.js';
import { useLocation, useNavigate } from "react-router-dom";

import 'react-phone-number-input/style.css';

// TODO: confirm endpoints + business token with the backend
const REGISTER_ENDPOINT = '/merchant/register_merchant';
const REQUIRED_DOCS_ENDPOINT = '/merchant/fetch_required_docs';
const UPLOAD_IMAGE_ENDPOINT = '/upload_image';
const BUSINESS_TOKEN = 'ZFOGFQKCi1X8GxjkC4RV2O04dAeT19zO';
const SUCCESS_ROUTE = '/corporate-register-success';

// ---- Palette: navy / bone / brass ---------------------------------------

export const c = {
    navy: '#0B1F3A',
    navyDeep: '#071527',
    navyMuted: 'rgba(255,255,255,0.62)',
    navyLine: 'rgba(255,255,255,0.12)',
    bone: '#F7F5F0',
    surface: '#FFFFFF',
    line: '#E6E2DA',
    lineStrong: '#C9C3B6',
    ink: '#14213D',
    inkSoft: '#5B6474',
    brass: '#A8875A',
    brassSoft: '#F3EDE2',
    error: '#B4443F',
};

export const theme = createTheme({
    palette: {
        primary: { main: c.navy },
        error: { main: c.error },
    },
    typography: {
        fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    },
    shape: { borderRadius: 6 },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: c.surface,
                    fontSize: '15px',
                    color: c.ink,
                    borderRadius: 6,
                    '& fieldset': { borderColor: c.line },
                    '&:hover fieldset': { borderColor: c.lineStrong },
                    '&.Mui-focused fieldset': { borderColor: c.navy, borderWidth: '1px' },
                    '&.Mui-error fieldset': { borderColor: c.error },
                },
                input: { padding: '13px 14px' },
            },
        },
        MuiFormHelperText: {
            styleOverrides: { root: { marginLeft: 0, fontSize: '12.5px' } },
        },
    },
});

// Loads display + body faces once (the host app may already do this).
export const useFonts = () => {
    useEffect(() => {
        if (document.getElementById('corp-onboarding-fonts')) return;
        const link = document.createElement('link');
        link.id = 'corp-onboarding-fonts';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500&family=DM+Sans:wght@400;500;600&display=swap';
        document.head.appendChild(link);
    }, []);
};

export const serif = '"Fraunces", Georgia, "Times New Roman", serif';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM = {
    merchantName: '',
    phone: '',
    email: '',
    alternativeEmail: '',
    password: '',
    rePassword: '',
    termsAccepted: false,
};

export const blankDoc = (doc) => ({
    document_id: doc.document_id,
    document_number: '',
    expiry_date: '',
    images: [],
});

// ---- Building blocks -----------------------------------------------------

export const FieldLabel = ({ children, required, hint }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '7px' }}>
        <Typography component="label" sx={{ fontSize: '13px', fontWeight: 500, color: c.ink, letterSpacing: '0.1px' }}>
            {children}
            {required && <Box component="span" sx={{ color: c.brass, ml: '3px' }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: '12px', color: c.inkSoft }}>{hint}</Typography>}
    </Box>
);

export const SectionTitle = ({ index, title, description }) => (
    <Grid size={12} sx={{ mt: index === '01' ? 0 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <Typography sx={{ fontFamily: serif, fontSize: '13px', color: c.brass, letterSpacing: '1.5px' }}>
                {index}
            </Typography>
            <Typography sx={{ fontFamily: serif, fontSize: '20px', fontWeight: 500, color: c.ink, letterSpacing: '-0.2px' }}>
                {title}
            </Typography>
        </Box>
        {description && (
            <Typography sx={{ fontSize: '13.5px', color: c.inkSoft, mt: '4px', ml: '30px' }}>
                {description}
            </Typography>
        )}
        <Box sx={{ height: '1px', backgroundColor: c.line, mt: '14px' }} />
    </Grid>
);

const Assurance = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Box sx={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0, mt: '1px',
            border: `1px solid ${c.brass}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <CheckIcon sx={{ fontSize: 12, color: c.brass }} />
        </Box>
        <Typography sx={{ fontSize: '14px', color: c.navyMuted, lineHeight: 1.55 }}>{children}</Typography>
    </Box>
);

export const PasswordToggle = ({ shown, onToggle }) => (
    <InputAdornment position="end">
        <IconButton
            size="small"
            onClick={onToggle}
            aria-label={shown ? 'Hide password' : 'Show password'}
            sx={{ color: c.inkSoft }}
        >
            {shown ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
        </IconButton>
    </InputAdornment>
);

function PermissionDeniedModal() {
    return (
      <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="denied-title">
        <div style={styles.modal}>
          <h2 id="denied-title" style={{ margin: 0 }}>Permission Denied</h2>
          <p style={{ margin: "12px 0 0" }}>
            You don't have permission to access this page. Please use the
            registration link provided to you.
          </p>
        </div>
      </div>
    );
  }

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    },
    modal: {
      background: "#fff",
      padding: "24px 28px",
      borderRadius: 8,
      maxWidth: 400,
      width: "90%",
      textAlign: "center",
      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    },
  };

// ---------------------------------------------------------------------------

const MerchantSelfRegister = () => {
    useFonts();

    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;
    const navigate = useNavigate();

    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRePassword, setShowRePassword] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ---- documents ----
    const [docs, setDocs] = useState([]);              // config from get_required_documents
    const [docValues, setDocValues] = useState([]);    // what the merchant entered, same order as docs
    const [docErrors, setDocErrors] = useState([]);
    const [docsLoading, setDocsLoading] = useState(true);
    const [docsFailed, setDocsFailed] = useState(false);
    const [uploadingKey, setUploadingKey] = useState('');
    const mountedRef = useRef(true);

    const location = useLocation();
    const search = location.search.replace(/\?/g, '&').replace('&', '?');
    const params = new URLSearchParams(search);
    const queryBusToken = params.get("t");
    const isTokenMissing = !queryBusToken || !queryBusToken.trim();
    const [tokenRejected, setTokenRejected] = useState(false);

    const notify = (message, severity = 'error') => setSnackbar({ open: true, message, severity });

    const setField = (field) => (eventOrValue) => {
        const value = eventOrValue && eventOrValue.target !== undefined ? eventOrValue.target.value : eventOrValue;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handlePhoneChange = (value) => {
        setForm((prev) => ({ ...prev, phone: value || '' }));
        setErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
    };

    // ---- load required documents ----
    const loadDocuments = async () => {
        setDocsLoading(true);
        setDocsFailed(false);
        try {
            const url = serverUrl + REQUIRED_DOCS_ENDPOINT;
            const response = await makeGetRequestToUrl(url, {
                business_token: queryBusToken,
                timestamp: new Date().getTime(),
            });
            if (!mountedRef.current) return;
            if (response.flag == 401) {
                setTokenRejected(true);
                return;
            }

            const list = (Array.isArray(response?.data) ? response.data : []).filter((d) => d.is_active === 1);
            setDocs(list);
            setDocValues(list.map(blankDoc));
            setDocErrors([]);
        } catch (error) {
            console.error('Error loading merchant documents:', error);
            if (mountedRef.current) setDocsFailed(true);
        } finally {
            if (mountedRef.current) setDocsLoading(false);
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        loadDocuments();
        return () => { mountedRef.current = false; };
    }, []);

    const updateDoc = (index, field, value) => {
        setDocValues((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
    };

    const clearDocError = (index, field) => {
        setDocErrors((prev) => {
            if (!prev[index]?.[field]) return prev;
            const next = [...prev];
            next[index] = { ...next[index] };
            delete next[index][field];
            return next;
        });
    };

    const handleFileChange = (index, imgIndex) => async (event) => {
        const input = event.target;
        const file = input.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            notify('Please choose an image file');
            input.value = '';
            return;
        }

        setUploadingKey(`${index}-${imgIndex}`);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(operationsUrl + UPLOAD_IMAGE_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const s3Url = response.data?.url;
            if (!s3Url) throw new Error('No URL returned');

            setDocValues((prev) => prev.map((d, i) => {
                if (i !== index) return d;
                const images = [...(d.images || [])];
                images[imgIndex] = s3Url;
                return { ...d, images };
            }));
            clearDocError(index, 'image');
        } catch (error) {
            console.error('Error uploading image:', error);
            notify('The upload didn\u2019t go through. Please try again.');
        } finally {
            setUploadingKey('');
            input.value = '';
        }
    };

    // ---- validation ----
    const validateDocuments = () => {
        const next = docs.map((doc, i) => {
            const d = docValues[i] || {};
            const images = (d.images || []).filter(Boolean);
            const started = Boolean(d.document_number?.trim() || d.expiry_date || images.length);
            const err = {};

            if (doc.is_required !== 1 && !started) return err;

            if (doc.is_doc_number_required === 1 && !d.document_number?.trim()) {
                err.documentNumber = 'Enter the document number';
            }
            if (doc.is_doc_expired === 1 && !d.expiry_date) {
                err.expiryDate = 'Choose the expiry date';
            }
            const needed = Number(doc.no_of_images) || 1;
            if (images.length < needed) {
                err.image = needed > 1 ? `Upload all ${needed} images` : 'Upload an image of this document';
            }
            return err;
        });

        setDocErrors(next);
        return next.every((e) => Object.keys(e).length === 0);
    };

    const validate = () => {
        const next = {};

        if (!form.merchantName.trim()) next.merchantName = 'Enter your business name';

        if (!form.phone) next.phone = 'Enter a contact number';
        else if (!isValidPhoneNumber(form.phone)) next.phone = 'This number looks invalid';

        if (!form.email.trim()) next.email = 'Enter an email address';
        else if (!EMAIL_REGEX.test(form.email.trim())) next.email = 'This email address looks invalid';

        if (form.alternativeEmail.trim()) {
            if (!EMAIL_REGEX.test(form.alternativeEmail.trim())) next.alternativeEmail = 'This email address looks invalid';
            else if (form.alternativeEmail.trim().toLowerCase() === form.email.trim().toLowerCase()) {
                next.alternativeEmail = 'Use a different address from the primary email';
            }
        }

        if (!form.password) next.password = 'Set a password';
        else if (form.password.length < 6) next.password = 'Use at least 6 characters';

        if (!form.rePassword) next.rePassword = 'Re-enter your password';
        else if (form.password && form.rePassword !== form.password) next.rePassword = 'Passwords don\u2019t match';

        if (!form.termsAccepted) next.termsAccepted = 'Please accept the terms to continue';

        setErrors(next);
        const docsOk = validateDocuments();
        return Object.keys(next).length === 0 && docsOk;
    };

    // ---- submit ----
    const submit = async () => {
        if (submitting) return;
        if (docsLoading || uploadingKey) {
            notify('Please wait for the uploads to finish');
            return;
        }
        if (docsFailed) {
            notify('We couldn\u2019t load the document list. Please retry before submitting.');
            return;
        }
        if (!validate()) {
            notify('Some details need your attention');
            return;
        }

        setSubmitting(true);
        try {
            const params = {
                merchant_name: form.merchantName.trim(),
                merchant_phone_number: form.phone,
                merchant_email: form.email.trim(),
                merchant_password: form.password,
                business_token: BUSINESS_TOKEN,
                merchant_docs: docValues
                    .filter((d) => d.document_number?.trim() || d.expiry_date || (d.images || []).some(Boolean))
                    .map((d) => ({
                        document_id: d.document_id,
                        document_number: d.document_number.trim(),
                        expiry_date: d.expiry_date,
                        images: (d.images || []).filter(Boolean),
                    })),
            };
            if (form.alternativeEmail.trim()) {
                params.alternative_email = form.alternativeEmail.trim();
            }

            const url = operationsUrl + REGISTER_ENDPOINT;
            const response = await makePostRequestToUrl(url, params);

            if (response?.flag === 200) {
                notify(response.message || 'Account created', 'success');
                setForm(INITIAL_FORM);
                setErrors({});
                setDocValues(docs.map(blankDoc));
                navigate(SUCCESS_ROUTE);
            } 
            else if (response?.flag == 410) {
                navigate('/merchant-add-documents?t=' + queryBusToken, {
                    state: { notification: { message: response?.message, severity: 'error' } },
                });
            }
            else {
                notify(response?.message || 'We couldn\u2019t complete your registration');
            }
        } catch (error) {
            console.error('Error while registering merchant:', error);
            notify(error?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const phoneInputOverrides = `
        .corp-phone .PhoneInputInput {
            border: none; outline: none; background: transparent;
            font-family: inherit; font-size: 15px; color: ${c.ink};
        }
        .corp-phone .PhoneInputCountry { margin-left: 14px; margin-right: 4px; }
        .corp-phone .PhoneInputCountrySelectArrow { color: ${c.inkSoft}; opacity: 1; }
    `;

    const hasRequiredDoc = docs.some((d) => d.is_required === 1);

    return (
        <>
        {(isTokenMissing || tokenRejected) && <PermissionDeniedModal />}
        {!isTokenMissing && !tokenRejected && (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <style>{phoneInputOverrides}</style>

                <Box sx={{ height: '100dvh', overflow: 'hidden', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, bgcolor: c.bone }}>
                    {/* ---- Left: brand panel ---------------------------------- */}
                    <Box sx={{
                        bgcolor: c.navy,
                        color: '#fff',
                        overflow: 'hidden',
                        px: { xs: 4, md: 6, lg: 8 },
                        py: { xs: 5, md: 8 },
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        position: 'relative',
                    }}>
                        <Box sx={{
                            position: 'absolute', inset: 0, pointerEvents: 'none',
                            background: `radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.06) 0%, transparent 60%)`,
                        }} />

                        <Box sx={{ position: 'relative' }}>
                            <Typography sx={{ fontSize: '12px', letterSpacing: '2.5px', textTransform: 'uppercase', color: c.brass, fontWeight: 500 }}>
                                Merchant accounts
                            </Typography>
                            <Typography sx={{
                                fontFamily: serif, fontWeight: 400,
                                fontSize: { xs: '34px', md: '40px', lg: '46px' },
                                lineHeight: 1.1, letterSpacing: '-0.5px', mt: 2.5, maxWidth: 420,
                            }}>
                                Bring your business on board.
                            </Typography>
                            <Typography sx={{ fontSize: '15.5px', color: c.navyMuted, lineHeight: 1.6, mt: 2.5, maxWidth: 400 }}>
                                Register as a merchant partner, upload your business documents once, and manage everything from a single portal after approval.
                            </Typography>
                        </Box>

                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ height: '1px', backgroundColor: c.navyLine, mb: 3.5 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Assurance>Upload your documents here — no paperwork by email</Assurance>
                                <Assurance>Every application is checked by our team before activation</Assurance>
                                <Assurance>Sign in with the email and password you set below once approved</Assurance>
                            </Box>
                            <Typography sx={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.4)', mt: 4 }}>
                                Keep your documents handy — registration takes a few minutes.
                            </Typography>
                        </Box>
                    </Box>

                    {/* ---- Right: form ---------------------------------------- */}
                    <Box sx={{
                        px: { xs: 2.5, sm: 5, lg: 9 }, py: { xs: 4, md: 7 },
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                        overflowY: 'auto', height: '100dvh',
                        scrollbarWidth: 'thin', scrollbarColor: `${c.lineStrong} transparent`,
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: c.lineStrong, borderRadius: 3 },
                    }}>
                        <Box sx={{ width: '100%', maxWidth: 640 }}>

                            <Box sx={{ mb: 4, display:'flex', alignItems:'center' }}>
                                <Grid>
                                    <Typography sx={{ fontFamily: serif, fontSize: '28px', fontWeight: 500, color: c.ink, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                                        Register as a merchant
                                    </Typography>
                                    <Typography sx={{ fontSize: '14.5px', color: c.inkSoft, mt: 1 }}>
                                        Fields marked <Box component="span" sx={{ color: c.brass }}>*</Box> are required.
                                    </Typography>
                                </Grid>
                                <Grid sx={{marginLeft:'auto'}}>
                                    <Typography onClick={() => navigate('/merchant-add-documents?t=' + queryBusToken)} sx={{textDecoration:'underline', cursor:'pointer'}}>Already registered?</Typography>
                                </Grid>
                            </Box>

                            <Box sx={{
                                bgcolor: c.surface,
                                border: `1px solid ${c.line}`,
                                borderRadius: '10px',
                                p: { xs: 3, sm: 4 },
                                boxShadow: '0 1px 0 rgba(20,33,61,0.03), 0 12px 32px -20px rgba(20,33,61,0.18)',
                            }}>
                                <Grid container spacing={2.5}>

                                    {/* ---- 01 Business ---- */}
                                    <SectionTitle index="01" title="Business" description="How we'll address you and reach you." />

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Business name</FieldLabel>
                                        <TextField
                                            placeholder="Registered business name"
                                            fullWidth
                                            value={form.merchantName}
                                            onChange={setField('merchantName')}
                                            error={Boolean(errors.merchantName)}
                                            helperText={errors.merchantName}
                                            autoComplete="organization"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Contact number</FieldLabel>
                                        <Box sx={{
                                            border: `1px solid ${errors.phone ? c.error : c.line}`,
                                            borderRadius: '6px', height: '48px', display: 'flex', alignItems: 'center',
                                            bgcolor: c.surface,
                                            '&:hover': { borderColor: errors.phone ? c.error : c.lineStrong },
                                            '&:focus-within': { borderColor: errors.phone ? c.error : c.navy },
                                        }}>
                                            <PhoneInput
                                                className="corp-phone"
                                                defaultCountry="IN"
                                                value={form.phone}
                                                onChange={handlePhoneChange}
                                                placeholder="Mobile number"
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </Box>
                                        {errors.phone && (
                                            <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '4px' }}>{errors.phone}</Typography>
                                        )}
                                    </Grid>

                                    {/* <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel hint="Optional">Alternative email</FieldLabel>
                                        <TextField
                                            placeholder="A backup contact address"
                                            fullWidth
                                            type="email"
                                            value={form.alternativeEmail}
                                            onChange={setField('alternativeEmail')}
                                            error={Boolean(errors.alternativeEmail)}
                                            helperText={errors.alternativeEmail}
                                        />
                                    </Grid> */}

                                    {/* ---- 02 Sign-in ---- */}
                                    <SectionTitle index="02" title="Sign-in" description="You'll use these to access the merchant portal." />

                                    <Grid size={12}>
                                        <FieldLabel required>Email</FieldLabel>
                                        <TextField
                                            placeholder="you@business.com"
                                            fullWidth
                                            type="email"
                                            value={form.email}
                                            onChange={setField('email')}
                                            error={Boolean(errors.email)}
                                            helperText={errors.email}
                                            autoComplete="email"
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required hint="Min. 6 characters">Password</FieldLabel>
                                        <TextField
                                            placeholder="Create a password"
                                            fullWidth
                                            type={showPassword ? 'text' : 'password'}
                                            value={form.password}
                                            onChange={setField('password')}
                                            error={Boolean(errors.password)}
                                            helperText={errors.password}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((s) => !s)} />,
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FieldLabel required>Re-enter password</FieldLabel>
                                        <TextField
                                            placeholder="Type it again"
                                            fullWidth
                                            type={showRePassword ? 'text' : 'password'}
                                            value={form.rePassword}
                                            onChange={setField('rePassword')}
                                            error={Boolean(errors.rePassword)}
                                            helperText={errors.rePassword}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: <PasswordToggle shown={showRePassword} onToggle={() => setShowRePassword((s) => !s)} />,
                                            }}
                                        />
                                    </Grid>

                                    {/* ---- 03 Documents ---- */}
                                    <SectionTitle
                                        index="03"
                                        title="Documents"
                                        description={hasRequiredDoc
                                            ? 'Clear photos of each document. Required ones are marked.'
                                            : 'Clear photos of each document you can provide.'}
                                    />

                                    <Grid size={12}>
                                        {docsLoading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                                                <CircularProgress size={18} sx={{ color: c.navy }} />
                                                <Typography sx={{ fontSize: '14px', color: c.inkSoft }}>Loading the document list…</Typography>
                                            </Box>
                                        ) : docsFailed ? (
                                            <Box sx={{ p: '14px 16px', borderRadius: '8px', border: `1px solid ${c.error}`, bgcolor: '#FBF1F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: '13.5px', color: c.ink }}>
                                                    We couldn't load the documents we need from you.
                                                </Typography>
                                                <Button onClick={loadDocuments} sx={{ textTransform: 'none', color: c.navy, fontWeight: 500 }}>
                                                    Try again
                                                </Button>
                                            </Box>
                                        ) : docs.length === 0 ? (
                                            <Typography sx={{ fontSize: '14px', color: c.inkSoft, py: 1 }}>
                                                No documents are needed right now.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {docs.map((doc, index) => {
                                                    const values = docValues[index] || {};
                                                    const err = docErrors[index] || {};
                                                    const hasError = Boolean(err.documentNumber || err.expiryDate || err.image);
                                                    const isRequired = doc.is_required === 1;
                                                    const imageCount = Number(doc.no_of_images) || 1;
                                                    const showNumber = doc.is_doc_number_required === 1;
                                                    const showExpiry = doc.is_doc_expired === 1;

                                                    return (
                                                        <Box key={doc.document_id} sx={{
                                                            border: `1px solid ${hasError ? c.error : c.line}`,
                                                            borderRadius: '8px',
                                                            bgcolor: c.bone,
                                                            p: { xs: 2, sm: 2.5 },
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
                                                                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>
                                                                    {doc.document_name}
                                                                    {isRequired && <Box component="span" sx={{ color: c.brass, ml: '3px' }}>*</Box>}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '12px', color: c.inkSoft }}>
                                                                    {isRequired ? 'Required' : 'Optional'}
                                                                </Typography>
                                                            </Box>

                                                            <Grid container spacing={2}>
                                                                {showNumber && (
                                                                    <Grid size={{ xs: 12, sm: showExpiry ? 6 : 12 }}>
                                                                        <FieldLabel required={isRequired}>Document number</FieldLabel>
                                                                        <TextField
                                                                            fullWidth
                                                                            placeholder="As printed on the document"
                                                                            value={values.document_number || ''}
                                                                            onChange={(e) => { updateDoc(index, 'document_number', e.target.value); clearDocError(index, 'documentNumber'); }}
                                                                            error={Boolean(err.documentNumber)}
                                                                            helperText={err.documentNumber}
                                                                        />
                                                                    </Grid>
                                                                )}

                                                                {showExpiry && (
                                                                    <Grid size={{ xs: 12, sm: showNumber ? 6 : 12 }}>
                                                                        <FieldLabel required={isRequired}>Expiry date</FieldLabel>
                                                                        <DatePicker
                                                                            disablePast
                                                                            format="DD/MM/YYYY"
                                                                            value={values.expiry_date ? dayjs(values.expiry_date) : null}
                                                                            onChange={(v) => {
                                                                                const ok = v && v.isValid();
                                                                                updateDoc(index, 'expiry_date', ok ? v.format('YYYY-MM-DD') : '');
                                                                                if (ok) clearDocError(index, 'expiryDate');
                                                                            }}
                                                                            slotProps={{
                                                                                textField: {
                                                                                    fullWidth: true,
                                                                                    placeholder: 'DD/MM/YYYY',
                                                                                    error: Boolean(err.expiryDate),
                                                                                    helperText: err.expiryDate,
                                                                                },
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                )}

                                                                <Grid size={12}>
                                                                    <FieldLabel
                                                                        required={isRequired}
                                                                        hint={imageCount > 1 ? `${imageCount} images, e.g. front and back` : undefined}
                                                                    >
                                                                        {imageCount > 1 ? 'Images' : 'Image'}
                                                                    </FieldLabel>

                                                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                                        {Array.from({ length: imageCount }).map((_, imgIndex) => {
                                                                            const inputId = `merchant-reg-${doc.document_id}-${imgIndex}`;
                                                                            const src = values.images?.[imgIndex];
                                                                            const busy = uploadingKey === `${index}-${imgIndex}`;

                                                                            return (
                                                                                <Box key={imgIndex} sx={{ width: 140 }}>
                                                                                    <input
                                                                                        id={inputId}
                                                                                        type="file"
                                                                                        accept="image/*"
                                                                                        capture={doc.is_gallery_restricted === 1 ? 'environment' : undefined}
                                                                                        style={{ display: 'none' }}
                                                                                        onChange={handleFileChange(index, imgIndex)}
                                                                                        disabled={busy}
                                                                                    />
                                                                                    <Box
                                                                                        component="label"
                                                                                        htmlFor={inputId}
                                                                                        sx={{
                                                                                            height: 110, borderRadius: '8px', cursor: busy ? 'default' : 'pointer',
                                                                                            border: `1px dashed ${err.image && !src ? c.error : src ? c.brass : c.lineStrong}`,
                                                                                            bgcolor: c.surface, overflow: 'hidden', position: 'relative',
                                                                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                                                                            transition: 'border-color 160ms ease',
                                                                                            '&:hover': { borderColor: busy ? undefined : c.navy },
                                                                                        }}
                                                                                    >
                                                                                        {busy ? (
                                                                                            <CircularProgress size={20} sx={{ color: c.navy }} />
                                                                                        ) : src ? (
                                                                                            <>
                                                                                                <Box component="img" src={src} alt={`${doc.document_name} ${imgIndex + 1}`}
                                                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                                                <Box sx={{
                                                                                                    position: 'absolute', bottom: 0, left: 0, right: 0, py: '4px',
                                                                                                    bgcolor: 'rgba(11,31,58,0.72)', color: '#fff', fontSize: '11.5px', textAlign: 'center',
                                                                                                }}>
                                                                                                    Replace
                                                                                                </Box>
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <FileUploadOutlinedIcon sx={{ fontSize: 22, color: c.inkSoft }} />
                                                                                                <Typography sx={{ fontSize: '12.5px', color: c.inkSoft }}>
                                                                                                    {imageCount > 1 ? `Image ${imgIndex + 1}` : 'Upload image'}
                                                                                                </Typography>
                                                                                            </>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                    {err.image && (
                                                                        <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '6px' }}>{err.image}</Typography>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Grid>

                                    {/* Terms */}
                                    <Grid size={12} sx={{ mt: 1 }}>
                                        <Box
                                            onClick={() => setField('termsAccepted')(!form.termsAccepted)}
                                            sx={{
                                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                                p: '14px 16px', borderRadius: '8px', cursor: 'pointer',
                                                bgcolor: form.termsAccepted ? c.brassSoft : c.bone,
                                                border: `1px solid ${errors.termsAccepted ? c.error : form.termsAccepted ? c.brass : c.line}`,
                                                transition: 'background-color 160ms ease, border-color 160ms ease',
                                            }}
                                        >
                                            <Checkbox
                                                checked={form.termsAccepted}
                                                onChange={(e) => setField('termsAccepted')(e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                                size="small"
                                                sx={{ p: 0, mt: '1px', color: c.lineStrong, '&.Mui-checked': { color: c.navy } }}
                                            />
                                            <Typography sx={{ fontSize: '13.5px', color: c.ink, lineHeight: 1.55 }}>
                                                I confirm I'm authorised to register this business, the documents are genuine, and I accept the{' '}
                                                <Box component="a" href="/terms" target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}
                                                    sx={{ color: c.navy, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                                    merchant terms and conditions
                                                </Box>.
                                            </Typography>
                                        </Box>
                                        {errors.termsAccepted && (
                                            <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '6px' }}>{errors.termsAccepted}</Typography>
                                        )}
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, flexWrap: 'wrap', gap: 2 }}>
                                    <Typography sx={{ fontSize: '12.5px', color: c.inkSoft }}>
                                        You'll receive a confirmation once your account is reviewed.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        disabled={submitting || !form.termsAccepted || Boolean(uploadingKey)}
                                        disableElevation
                                        onClick={submit}
                                        endIcon={!submitting && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                                        sx={{
                                            bgcolor: c.navy, color: '#fff',
                                            textTransform: 'none', fontWeight: 500, fontSize: '15px',
                                            borderRadius: '6px', minWidth: 200, height: 48, px: 3,
                                            '&:hover': { bgcolor: c.navyDeep },
                                            '&.Mui-disabled': { bgcolor: c.navy, color: '#fff', opacity: 0.7 },
                                        }}
                                    >
                                        {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create merchant account'}
                                    </Button>
                                </Box>
                            </Box>

                            <Typography sx={{ fontSize: '12.5px', color: c.inkSoft, textAlign: 'center', mt: 3 }}>
                                Already registered?{' '}
                                <Box component="a" href="/signup" sx={{ color: c.navy, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                    Sign in to your portal
                                </Box>
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '8px' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </LocalizationProvider>
        </ThemeProvider>
        )}
        </>
    );
};

export default MerchantSelfRegister;